import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreHorizontal, Search, Copy, Route } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useVirtualRouters,
  useDeleteVirtualRouter,
  type VirtualRouter,
} from '@/api/opennebula';
import { RouterCreateDialog } from './RouterCreateDialog';

/** Maps router lifecycle status to a display tone. */
function routerTone(status?: string): StatusTone {
  const v = (status ?? '').toLowerCase();
  if (v === 'active' || v === 'running') return 'success';
  if (/error|fail/.test(v)) return 'danger';
  if (/pend|deploy|creat/.test(v)) return 'warning';
  return 'muted';
}

export function VirtualRouters({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const base = `/services/compute/${serviceId}`;
  const { data: routers = [], isLoading } = useVirtualRouters(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState<VirtualRouter | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return routers;
    return routers.filter((r) =>
      [r.name, r.id, r.status, r.public_ip].some((f) => String(f ?? '').toLowerCase().includes(q)),
    );
  }, [routers, query]);

  const remove = useDeleteVirtualRouter(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Router deleted' }); setDel(null); },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  function copy(value: string) {
    navigator.clipboard?.writeText(value);
    show({ tone: 'success', title: 'Copied to clipboard' });
  }

  const columns: ColumnDef<VirtualRouter, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || row.original.id}</span> },
    { id: 'status', header: 'State', cell: ({ row }) => <StatusPill tone={routerTone(row.original.status)}>{row.original.status || 'Unknown'}</StatusPill> },
    {
      // External NAT public IP (mirrors legacy); the list payload has no NIC
      // count, so a "networks" column would always read 0 — show it on detail.
      id: 'public_ip',
      header: 'External NAT',
      cell: ({ row }) => row.original.public_ip ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }} onClick={(e) => e.stopPropagation()}>
          <code style={{ color: 'var(--vn-fg-muted)' }}>{row.original.public_ip}</code>
          <IconButton variant="ghost" size="sm" aria-label="Copy IP" onClick={() => copy(row.original.public_ip!)}><Copy size={13} /></IconButton>
        </span>
      ) : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Router actions"><MoreHorizontal size={16} /></IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => navigate(`${base}/networks/routers/${row.original.id}`)}>Manage</DropdownMenuItem>
              <DropdownMenuItem tone="danger" onSelect={() => setDel(row.original)}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search routers" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 320 }} />}
        end={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New router</Button>}
      />
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(r) => String(r.id)}
          onRowClick={(r) => navigate(`${base}/networks/routers/${r.id}`)}
          emptyState={
            routers.length === 0 ? (
              <EmptyState
                icon={<Route size={28} />}
                title="No virtual routers yet"
                description="Create a router to connect networks and add static routes."
                actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New router</Button>}
              />
            ) : (
              'No routers match your search.'
            )
          }
        />
      )}
      <RouterCreateDialog serviceId={serviceId} open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete router?"
        description={`Remove “${del?.name || del?.id}”?`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </>
  );
}
